import React from 'react'
import { ApolloClient, ApolloLink, InMemoryCache, ApolloProvider, NormalizedCacheObject, fromPromise } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'

import { getNewToken, addUserToken, getHeadersContext } from './utils'
import { isProd } from 'utils'

export const ApolloClientProvider: React.FC = ({ children }) => {
    const uploadLink = createUploadLink({
        uri: process.env.BFF_URL,
    })
    const authLink = setContext(async (_, { headers }) => getHeadersContext(headers))

    const errorLink = onError(({ graphQLErrors, operation, forward }) => {
        if (operation.operationName === 'refreshToken') {
            return
        }
        if (graphQLErrors) {
            for (const error of graphQLErrors) {
                switch (error?.extensions?.code) {
                    case 'UNAUTHENTICATED':
                        return fromPromise(getNewToken(client))
                            .filter(value => Boolean(value.accessToken))
                            .flatMap(({ accessToken, refreshToken }) => {
                                const { headers: oldHeaders } = operation.getContext()

                                addUserToken(accessToken, refreshToken)

                                operation.setContext({
                                    headers: {
                                        ...oldHeaders,
                                        authorization: `Bearer ${accessToken}`,
                                    },
                                })
                                return forward(operation)
                            })
                    case 'UNAUTHORIZED':
                        continue
                    default:
                        break
                }
            }
        }
        return
    })

    const link = ApolloLink.from([authLink, errorLink, (uploadLink as unknown) as ApolloLink])

    const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
        link: link,
        cache: new InMemoryCache(),
        connectToDevTools: !isProd(),
    })

    return <ApolloProvider client={client}>{children}</ApolloProvider>
}
